import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { Person } from '@material-ui/icons';
import { useQueryContext } from '@sierralabs/query-ui';
import { TaskStatusDot, TaskStatusLabel } from '@sierralabs/workflow-ui';
import { getQueryValue } from '../component/useQueryDisplayValue';
import { useSiteRouteComponents } from '../site-route/useSiteRouteComponents';
const StyledSubContent = styled.div `
  display: flex;
  flex-direction: column;
  padding: 0 12px 12px 12px;
  .assigned {
    display: flex;
    align-items: center;
    .icon-container {
      width: 36px;
      height: 36px;
      margin-right: 10px;
      border-radius: 18px;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      background-color: ${props => props.theme.palette.grey[200]};
    }
    .text-container {
      flex: 1;
    }
  }
`;
// override columns and include render functions
export function useSiteKanbanBoardColumns(component, columns) {
    var _a;
    const { queries } = useQueryContext();
    const routes = useSiteRouteComponents();
    // use key to re-evaluate when columns are updated
    // since the columns array does not change reference
    const columnsKey = (((_a = component.props) === null || _a === void 0 ? void 0 : _a.columns) || [])
        .map(column => {
        // trigger updates when these values change
        const fields = column.itemFields || {};
        const filter = column.filter || {};
        const nav = column.routeNavigation || {};
        const keys = [
            column.id,
            column.queryId,
            column.workflowPath,
            column.filterByStatus,
            fields.status,
            fields.workflowId,
            fields.taskId,
            fields.firstName,
            fields.lastName,
            filter.field,
            filter.routeId,
            filter.routeParam,
            nav.field,
            nav.routeId,
            nav.routeParam
        ];
        return keys.join(' ');
    })
        .join(',');
    const componentColumns = useMemo(() => {
        var _a;
        return Array.from(((_a = component.props) === null || _a === void 0 ? void 0 : _a.columns) || []);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columnsKey]);
    const getColumnQuery = useCallback((columnId) => {
        const column = componentColumns.find(column => column.id === columnId);
        const queryId = column === null || column === void 0 ? void 0 : column.queryId;
        return queryId ? queries.find(query => query.id === queryId) : undefined;
    }, [componentColumns, queries]);
    const getTaskFields = useCallback((item) => {
        const query = getColumnQuery(item.columnId);
        const column = componentColumns.find(column => column.id === item.columnId);
        const fields = (column === null || column === void 0 ? void 0 : column.itemFields) || {};
        const status = query && fields.status
            ? getQueryValue(item.data, query, fields.status)
            : undefined;
        const workflowId = query && fields.workflowId
            ? getQueryValue(item.data, query, fields.workflowId)
            : undefined;
        return { status, workflowId };
    }, [componentColumns, getColumnQuery]);
    // title icon
    const renderTitleIcon = useCallback((renderOptions) => {
        const { item } = renderOptions;
        const task = getTaskFields(item);
        return task.status ? React.createElement(TaskStatusDot, { task: getTaskFields(item) }) : null;
    }, [getTaskFields]);
    // title accessory
    const renderTitleAccessory = useCallback((renderOptions) => {
        const { item } = renderOptions;
        const task = getTaskFields(item);
        return task.status ? (React.createElement(TaskStatusLabel, { size: 'small', task: getTaskFields(item) })) : null;
    }, [getTaskFields]);
    // sub content
    const getItemPath = useCallback((item) => {
        var _a;
        const query = getColumnQuery(item.columnId);
        const column = componentColumns.find(column => column.id === item.columnId);
        const nav = (column === null || column === void 0 ? void 0 : column.routeNavigation) || {};
        const route = nav.routeId
            ? routes.find(route => route.id === nav.routeId)
            : undefined;
        const routePath = ((_a = route === null || route === void 0 ? void 0 : route.props) === null || _a === void 0 ? void 0 : _a.path) || '';
        if (!item.data || !nav.field || !query || !routePath) {
            return;
        }
        const value = getQueryValue(item.data, query, nav.field);
        const path = routePath.replace(`:${nav.routeParam}`, value);
        return path;
    }, [componentColumns, routes, getColumnQuery]);
    const renderSubContent = useCallback((renderOptions) => {
        const { item } = renderOptions;
        if (!item.data || !item.assignee) {
            return null;
        }
        return (React.createElement(StyledSubContent, null,
            React.createElement("div", { className: 'assigned' },
                React.createElement("div", { className: 'icon-container' },
                    React.createElement(Person, null)),
                React.createElement("div", { className: 'text-container' },
                    item.assignee.firstName,
                    " ",
                    item.assignee.lastName))));
    }, []);
    const updatedColumns = useMemo(() => {
        return columns.map(column => {
            column = Object.assign(Object.assign({}, column), { renderSubContent,
                renderTitleIcon,
                renderTitleAccessory });
            return column;
        });
    }, [columns, renderSubContent, renderTitleAccessory, renderTitleIcon]);
    return { columns: updatedColumns, componentColumns, getItemPath };
}
//# sourceMappingURL=useSiteKanbanBoardColumns.js.map